import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import color from 'tinycolor2';
import { ModalContent } from 'components/Modal';

/* ---------- theme helpers ---------- */

const textBodyAlpha = (theme, alpha) => color(theme['color.text.body']).setAlpha(alpha).toString();
const primaryAlpha = (theme, alpha) =>
  color(theme['color.primary'] || '#0070D2').setAlpha(alpha).toString();
const surfaceColor = (theme) => theme['color.surface'] || '#FFFFFF';
const borderColor = (theme) => theme['color.greyLight'] || '#E5E7EB';

const modalScrollbarStyle = css`
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  scrollbar-width: thin;
  scrollbar-color: ${(props) => textBodyAlpha(props.theme, 0.22)} transparent;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background-color: ${(props) => textBodyAlpha(props.theme, 0.14)};
    transition: background-color 0.25s ease;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: ${(props) => textBodyAlpha(props.theme, 0.28)};
  }

  &::-webkit-scrollbar-thumb:active {
    background-color: ${(props) => textBodyAlpha(props.theme, 0.4)};
  }
`;

/* ---------- shimmer / skeleton ---------- */

/*
 * Seamless shimmer:
 * The gradient is 200% the element width with the highlight band
 * placed in the middle and the edges sharing the same flat base
 * color. Sliding `background-position` from 100% to -100% covers a
 * full gradient sweep, and because both extremes show the flat base,
 * the loop has no visible "reset" jump.
 */
const shimmerAnimation = keyframes`
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
`;

const shimmerSurface = css`
  background-color: ${(props) => textBodyAlpha(props.theme, 0.06)};
  background-image: linear-gradient(
    90deg,
    ${(props) => textBodyAlpha(props.theme, 0.06)} 0%,
    ${(props) => textBodyAlpha(props.theme, 0.06)} 35%,
    ${(props) => textBodyAlpha(props.theme, 0.16)} 50%,
    ${(props) => textBodyAlpha(props.theme, 0.06)} 65%,
    ${(props) => textBodyAlpha(props.theme, 0.06)} 100%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: ${shimmerAnimation} 1.8s linear infinite;
  will-change: background-position;
  border-radius: 4px;
`;

export const ShimmerBlock = styled.div`
  ${shimmerSurface}
  width: ${(props) => props.width || '100%'};
  height: ${(props) => props.height || '14px'};
`;

const cardBase = css`
  border: 1px solid ${(props) => borderColor(props.theme)};
  border-radius: 6px;
  background-color: ${(props) => surfaceColor(props.theme)};
`;

export const ShimmerCard = styled.div`
  ${cardBase}
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ShimmerStat = styled.div`
  ${cardBase}
  padding: 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ShimmerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 4px;
  border-bottom: 1px solid ${(props) => textBodyAlpha(props.theme, 0.08)};

  &:last-child {
    border-bottom: none;
  }
`;

export const ShimmerTagGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

/* ---------- modal layout ---------- */

export const AssessmentModalContent = styled(ModalContent)`
  padding: 8px 30px 30px;
  max-height: 70vh;
  overflow-y: auto;
  ${modalScrollbarStyle}
`;

export const Subtitle = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: ${(props) => textBodyAlpha(props.theme, 0.7)};
`;

export const Section = styled.section`
  margin-bottom: 20px;
`;

export const SectionLabel = styled.h2`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${(props) => textBodyAlpha(props.theme, 0.7)};
  margin: 0 0 10px;
`;

/* ---------- learning context card ---------- */

export const ContextCard = styled.div`
  ${cardBase}
  padding: 16px 20px;
  background-color: #fafbfd;
`;

export const ContextRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  align-items: start;
  padding: 6px 0;
  font-size: 14px;

  &:last-child {
    border-bottom: none;
  }
`;

export const ContextLabel = styled.span`
  font-weight: 600;
  color: ${(props) => textBodyAlpha(props.theme, 0.7)};
`;

export const ContextValue = styled.span`
  color: ${(props) => props.theme['color.text.body']};
  word-break: break-all;
`;

/* ---------- summary stats ---------- */

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 20px;
`;

export const StatCard = styled.div`
  ${cardBase}
  padding: 14px 18px;
  background-color: #fafbfd;
`;

export const StatLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => textBodyAlpha(props.theme, 0.7)};
  margin-bottom: 6px;
`;

export const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => props.theme['color.text.body']};
`;

/* ---------- assessment list ---------- */

export const AssessmentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const AssessmentItem = styled.li`
  border-bottom: 1px solid ${(props) => textBodyAlpha(props.theme, 0.08)};

  &:last-child {
    border-bottom: none;
  }
`;

export const AssessmentHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: inherit;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: ${(props) => textBodyAlpha(props.theme, 0.02)};
  }
`;

export const DocIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 22px;
  flex-shrink: 0;
  color: ${(props) => textBodyAlpha(props.theme, 0.6)};

  & svg {
    width: 100%;
    height: 100%;
  }
`;

export const AssessmentTitle = styled.div`
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme['color.text.body']};
`;

export const TagGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const baseTag = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 14px;
  border: 1px solid ${(props) => borderColor(props.theme)};
  background-color: ${(props) => surfaceColor(props.theme)};
  color: ${(props) => props.theme['color.text.body']};
  white-space: nowrap;
`;

export const Tag = styled.span`
  ${baseTag}
`;

export const SoftTag = styled.span`
  ${baseTag}
  background-color: ${(props) => primaryAlpha(props.theme, 0.12)};
  border-color: ${(props) => primaryAlpha(props.theme, 0.18)};
  color: ${(props) => props.theme['color.text.body']};
`;

export const PrimaryTag = styled.span`
  ${baseTag}
  background-color: ${(props) => props.theme['color.text.body']};
  color: ${(props) => surfaceColor(props.theme)};
  border-color: ${(props) => props.theme['color.text.body']};
`;

/* `expanded` is consumed for styling only — strip it before it hits the DOM. */
export const ChevronButton = styled(({ expanded, ...rest }) => <span {...rest} />)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${(props) => textBodyAlpha(props.theme, 0.6)};
  transform: ${(props) => (props.expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s ease;

  & svg {
    width: 14px;
    height: 14px;
  }
`;

/* ---------- expanded items (cards) ---------- */

export const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 4px 16px 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ItemRow = styled.li`
  ${cardBase}
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 8px;
  box-shadow: 0 1px 2px ${(props) => textBodyAlpha(props.theme, 0.04)};
`;

export const ItemMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ItemTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme['color.text.body']};
`;

export const ItemUrn = styled.div`
  font-size: 12px;
  color: ${(props) => textBodyAlpha(props.theme, 0.6)};
  word-break: break-all;
`;
